import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Globe, ShoppingBag, Search, Asterisk } from "lucide-react";
import { Reveal, Overline, EASE } from "@/components/Motion";
import EditorialMarquee from "@/components/Marquee";
import { api, imgSrc } from "@/lib/api";

const HERO_BG =
  "https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRpZ2l0YWwlMjAzZCUyMGdyYWRpZW50fGVufDB8fHx8MTc4NzY3MzgwNnww&ixlib=rb-4.1.0&q=85";

const SERVICES_PREVIEW = [
  { icon: Globe, title: "Website Design", desc: "Custom, lightning-fast sites built to convert visitors into customers." },
  { icon: ShoppingBag, title: "E-commerce", desc: "Storefronts engineered for sales — smooth checkout, zero friction." },
  { icon: Search, title: "SEO", desc: "Get found on Google by the customers already looking for you." },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  const [featured, setFeatured] = useState(null);
  useEffect(() => {
    api.get("/projects").then((r) => setFeatured(r.data[0] || null)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* ---------- HERO ---------- */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden" data-testid="home-hero">
        <div className="absolute -top-40 -right-40 w-[36rem] h-[36rem] rounded-full bg-brand-purple/25 blur-[150px] -z-10" />
        <div className="absolute bottom-0 -left-48 w-[30rem] h-[30rem] rounded-full bg-brand-blue/20 blur-[150px] -z-10" />
        <div
          className="absolute inset-0 -z-10 opacity-[0.35]"
          style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.07) 1px, transparent 1px)", backgroundSize: "36px 36px" }}
        />

        {/* vertical edge label */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="hidden lg:block absolute left-10 top-1/2 -translate-y-1/2 -rotate-90 origin-left text-[11px] uppercase tracking-[0.4em] text-white/30 whitespace-nowrap"
        >
          Zilleneal Digital — Est. Alberta
        </motion.p>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-20 w-full grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
              className="flex items-center gap-3"
            >
              <motion.span animate={{ rotate: 360 }} transition={{ duration: 6, repeat: Infinity, ease: "linear" }} className="inline-flex">
                <Asterisk className="w-5 h-5 text-brand-cyan" />
              </motion.span>
              <Overline data-testid="hero-overline">Zilleneal Digital — Alberta Web Studio</Overline>
            </motion.div>

            <h1
              data-testid="hero-headline"
              className="mt-8 font-sora font-extrabold tracking-tighter leading-[0.95] text-6xl sm:text-7xl lg:text-[6.5rem]"
            >
              {[
                { text: "Modern", cls: "text-white" },
                { text: "Websites", cls: "text-gradient" },
                { text: "for Modern", cls: "text-outline" },
                { text: "Businesses.", cls: "text-white" },
              ].map((line, i) => (
                <span key={i} className="block overflow-hidden pb-[0.09em] -mb-[0.09em]">
                  <motion.span
                    className={`block ${line.cls}`}
                    initial={{ y: "115%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 1, delay: 0.25 + i * 0.13, ease: EASE }}
                  >
                    {line.text}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
              className="mt-8 max-w-xl text-base md:text-lg text-white/60 leading-relaxed"
              data-testid="hero-subcopy"
            >
              We design and build fast, beautiful websites for small businesses that want to
              look big — and win bigger. Design, code and SEO under one roof.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-cta-quote"
                className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-brand-purple px-8 py-4 font-semibold transition-transform duration-300 hover:scale-[1.04]"
              >
                Get a Free Quote
                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/work"
                data-testid="hero-cta-work"
                className="group inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 font-semibold text-white/80 transition-colors duration-300 hover:border-brand-cyan/60 hover:text-white"
              >
                See Our Work
                <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.25 }}
              className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-white/45"
              data-testid="hero-meta"
            >
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-brand-cyan animate-pulse-dot" />
                Available for new projects
              </span>
              <span>Based in Alberta, Canada</span>
              <span>Serving clients everywhere</span>
            </motion.div>
          </div>

          {/* clipped visual + spinning stamp */}
          <div className="lg:col-span-5 relative hidden lg:block">
            <motion.div
              initial={{ opacity: 0, y: 60, rotate: 3 }}
              animate={{ opacity: 1, y: 0, rotate: 1.5 }}
              transition={{ duration: 1.2, delay: 0.6, ease: EASE }}
              style={{ y: cardY }}
              className="relative rounded-[2rem] overflow-hidden border border-white/15"
            >
              <img src={HERO_BG} alt="Abstract digital artwork" className="w-full aspect-[3/4] object-cover" loading="eager" />
              <div className="absolute inset-0 bg-gradient-to-t from-midnight/60 via-transparent to-transparent" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
              className="absolute -bottom-10 -left-12 w-36 h-36 rounded-full bg-midnight border border-white/15 flex items-center justify-center"
            >
              <motion.svg
                viewBox="0 0 100 100"
                className="absolute inset-0 w-full h-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <defs>
                  <path id="stamp-circle" d="M 50,50 m -36,0 a 36,36 0 1,1 72,0 a 36,36 0 1,1 -72,0" />
                </defs>
                <text className="fill-white/60 uppercase" style={{ fontSize: "8.5px", letterSpacing: "2.6px" }}>
                  <textPath href="#stamp-circle">Alberta Web Studio • Zilleneal •</textPath>
                </text>
              </motion.svg>
              <Asterisk className="w-7 h-7 text-brand-cyan" strokeWidth={1.5} />
            </motion.div>
          </div>
        </div>
      </section>

      <EditorialMarquee
        items={["Web Design", "E-Commerce", "SEO", "Branding", "Web Apps", "Digital Marketing"]}
      />

      {/* ---------- CREDIBILITY STRIP ---------- */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="home-credibility">
        <div className="grid md:grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden">
          {[
            ["01", "Design + code, one roof", "No hand-offs, no agencies-in-the-middle. The person you talk to builds your site."],
            ["02", "Launch in weeks, not months", "A lean process that ships fast without cutting corners on craft."],
            ["03", "SEO baked into every build", "Structured, fast and search-ready from day one — not bolted on later."],
          ].map(([num, title, desc], i) => (
            <Reveal key={num} delay={i * 0.12} className="bg-surface p-10">
              <p className="font-sora text-sm text-gradient font-bold">{num}</p>
              <h3 className="font-sora font-bold text-xl mt-4 tracking-tight">{title}</h3>
              <p className="text-sm text-white/55 mt-3 leading-relaxed">{desc}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- SERVICES PREVIEW ---------- */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="home-services-preview">
        <Reveal>
          <Overline>What we do</Overline>
          <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-sora font-extrabold tracking-tighter text-4xl sm:text-5xl max-w-xl leading-[1.05]">
              Everything your business needs to <span className="text-gradient">win online</span>
            </h2>
            <Link
              to="/services"
              data-testid="services-preview-link"
              className="group inline-flex items-center gap-2 text-sm font-semibold text-white/70 hover:text-brand-cyan transition-colors duration-300"
            >
              All services <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {SERVICES_PREVIEW.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.12}>
              <Link
                to="/services"
                data-testid={`service-preview-${s.title.toLowerCase().replace(/\s/g, "-")}`}
                className="glow-border group block rounded-2xl border border-white/10 bg-surface p-10 h-full transition-transform duration-500 hover:-translate-y-1.5"
              >
                <s.icon className="w-8 h-8 text-brand-cyan" strokeWidth={1.5} />
                <h3 className="font-sora font-bold text-2xl mt-8 tracking-tight">{s.title}</h3>
                <p className="text-sm text-white/55 mt-3 leading-relaxed">{s.desc}</p>
                <ArrowUpRight className="w-5 h-5 mt-8 text-white/30 transition-all duration-300 group-hover:text-brand-cyan group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- FEATURED PROJECT ---------- */}
      {featured && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="home-featured-project">
          <Reveal>
            <Overline>Featured work</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/work" data-testid="featured-project-card" data-cursor="view" className="group block mt-8">
              <div className="relative rounded-3xl overflow-hidden border border-white/10">
                <div className="aspect-[16/8] overflow-hidden">
                  <img
                    src={imgSrc(featured.image_url)}
                    alt={featured.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.05]"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-midnight via-midnight/30 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-8 md:p-14 flex flex-wrap items-end justify-between gap-6">
                  <div>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {featured.tags.map((t) => (
                        <span key={t} className="text-xs uppercase tracking-[0.15em] border border-white/25 rounded-full px-3 py-1 text-white/70 backdrop-blur-sm">
                          {t}
                        </span>
                      ))}
                    </div>
                    <h3 className="font-sora font-extrabold tracking-tighter text-4xl md:text-6xl">{featured.title}</h3>
                  </div>
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-xl border border-white/15 px-6 py-3 text-sm font-semibold transition-colors duration-300 group-hover:bg-brand-blue group-hover:border-brand-blue">
                    View case study <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </Link>
          </Reveal>
        </section>
      )}

      {/* ---------- CTA BAND ---------- */}
      <section className="relative overflow-hidden" data-testid="home-cta-band">
        <div className="absolute inset-0 bg-gradient-to-r from-brand-blue/15 to-brand-purple/15" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-32 text-center">
          <Reveal>
            <h2 className="font-sora font-extrabold tracking-tighter text-4xl sm:text-6xl leading-[1.02]">
              Your competitors are online.
              <span className="text-gradient block mt-2">Are you?</span>
            </h2>
            <p className="mt-6 text-white/60 max-w-lg mx-auto">
              Tell us about your project — we'll reply within one business day with honest advice and a clear quote.
            </p>
            <Link
              to="/contact"
              data-testid="cta-band-quote"
              className="group mt-10 inline-flex items-center gap-2 rounded-full bg-white text-midnight px-9 py-4 font-bold transition-transform duration-300 hover:scale-[1.05]"
            >
              Start your project
              <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
